import React, { useState } from 'react';
import { Image as ImageIcon, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';

/**
 * ShareCardPreviewButton
 *
 * A single "See my share card" button that opens the EXACT 1200×630 social
 * preview image (served by /api/og/...) in a dialog, so the signed-in talent
 * or business can see precisely what Facebook / WhatsApp / LinkedIn show
 * before they post — and download it as an image if they want to.
 *
 * `imageUrl` is the OG card endpoint for this account. We append a timestamp
 * on open so the preview always reflects the latest profile (never a stale
 * browser-cached copy).
 */
const ShareCardPreviewButton = ({
  imageUrl,
  label = 'See my share card',
  className = '',
  variant = 'outline',
  size,
  fileName = 'ievntur-share-card.png',
  testid = 'share-card-preview',
}) => {
  const [open, setOpen] = useState(false);
  const [bust, setBust] = useState(0);
  const [loaded, setLoaded] = useState(false);

  if (!imageUrl) return null;

  const freshUrl = `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}t=${bust}`;

  const handleOpen = () => {
    setBust(Date.now());
    setLoaded(false);
    setOpen(true);
  };

  const download = async () => {
    try {
      const res = await fetch(freshUrl);
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
      toast.success('Share card image downloaded');
    } catch {
      toast.error('Could not download — try again.');
    }
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={handleOpen}
        className={`rounded-xl ${className}`}
        data-testid={`${testid}-button`}
      >
        <ImageIcon className="w-4 h-4 mr-1.5" /> {label}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid={`${testid}-dialog`}>
          <DialogHeader>
            <DialogTitle>Your social share card</DialogTitle>
            <DialogDescription>
              This is exactly what people see when you share your link on Facebook,
              WhatsApp, LinkedIn, Messenger and more.
            </DialogDescription>
          </DialogHeader>

          <div className="relative w-full rounded-xl overflow-hidden border border-slate-200 bg-black" style={{ aspectRatio: '1200 / 630' }}>
            {!loaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-cyan-400" />
              </div>
            )}
            <img
              src={freshUrl}
              alt="Your iEvntür social share card"
              className="w-full h-full object-contain"
              onLoad={() => setLoaded(true)}
              data-testid={`${testid}-image`}
            />
          </div>

          <div className="flex justify-end">
            <Button onClick={download} className="rounded-xl bg-gray-900 hover:bg-gray-800 text-white" data-testid={`${testid}-download`}>
              <Download className="w-4 h-4 mr-1.5" /> Download image
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ShareCardPreviewButton;
